import { useEffect, useState } from 'react';
import { Panel, Flex, Button, CellList, CellSimple, CellHeader, Typography, Spinner } from '@maxhub/max-ui';
import { getReceiptsByHouse, generateReceipt } from '../api/receipts.ts';
import { useHousesStore } from '../store/houses.store.ts';
import { formatPeriod, getCurrentPeriod } from '../utils/period.ts';
import type { Receipt } from '../types/models.ts';

interface ReceiptsPageProps {
  houseId: string;
  onBack: () => void;
  onViewReceipt: (receiptId: string) => void;
}

export function ReceiptsPage({ houseId, onBack, onViewReceipt }: ReceiptsPageProps) {
  const { houses } = useHousesStore();
  const house = houses.find((h) => h.id === houseId);

  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [genError, setGenError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    getReceiptsByHouse(houseId)
      .then(setReceipts)
      .catch(() => setError('Не удалось загрузить квитанции'))
      .finally(() => setLoading(false));
  }, [houseId]);

  async function handleGenerate() {
    setGenError(null);
    setGenerating(true);
    try {
      const receipt = await generateReceipt(houseId, getCurrentPeriod());
      setReceipts((prev) => [receipt, ...prev]);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Не удалось сформировать квитанцию';
      setGenError(msg);
    } finally {
      setGenerating(false);
    }
  }

  const isPrimaryOwner = house?.myRole === 'primary_owner';

  return (
    <Panel style={{ padding: '1rem', maxWidth: 480, margin: '0 auto' }}>
      {/* Header */}
      <Flex justify="space-between" align="center" style={{ marginBottom: '0.5rem' }}>
        <Button mode="link" onClick={onBack}>← Назад</Button>
        <Typography.Headline variant="medium-strong">Квитанции</Typography.Headline>
        <div style={{ width: 60 }} />
      </Flex>

      {house && (
        <Typography.Label style={{ display: 'block', opacity: 0.6, marginBottom: '1rem' }}>
          {house.fullAddress ?? `Дом ${house.buildingNumber}`}
        </Typography.Label>
      )}

      {/* Кнопка генерации */}
      {isPrimaryOwner && (
        <div style={{ marginBottom: '1rem' }}>
          <Button
            mode="primary"
            stretched
            loading={generating}
            disabled={generating}
            onClick={handleGenerate}
          >
            {generating ? 'Формирование...' : `Сформировать за ${formatPeriod(getCurrentPeriod())}`}
          </Button>
          {genError && (
            <Typography.Label style={{ display: 'block', color: '#c62828', marginTop: '0.5rem' }}>
              {genError}
            </Typography.Label>
          )}
        </div>
      )}

      {/* Список */}
      {loading ? (
        <Panel centeredX style={{ padding: '3rem 0' }}>
          <Spinner size={24} />
        </Panel>
      ) : error ? (
        <Typography.Label style={{ display: 'block', opacity: 0.6, textAlign: 'center', padding: '2rem 0' }}>
          {error}
        </Typography.Label>
      ) : receipts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '2rem 1rem', color: '#666' }}>
          <Typography.Label style={{ display: 'block' }}>Квитанций пока нет.</Typography.Label>
          {isPrimaryOwner && (
            <Typography.Label style={{ display: 'block', opacity: 0.6, marginTop: '0.5rem' }}>
              Нажмите «Сформировать» для создания первой квитанции.
            </Typography.Label>
          )}
        </div>
      ) : (
        <CellList mode="island">
          <CellHeader>Квитанции</CellHeader>
          {receipts.map((r) => (
            <CellSimple
              key={r.id}
              title={formatPeriod(r.period)}
              subtitle={`№ ${r.receiptNumber}`}
              after={
                <Flex direction="column" align="flex-end" gap={4}>
                  <Typography.Headline variant="medium-strong">
                    {Number(r.totalAmount).toFixed(2)} ₽
                  </Typography.Headline>
                  <Flex gap={6}>
                    <Button mode="secondary" size="small" onClick={() => onViewReceipt(r.id)}>
                      Подробнее
                    </Button>
                    {r.pdfUrl && (
                      <Button mode="secondary" size="small" asChild>
                        <a href={r.pdfUrl} target="_blank" rel="noreferrer">
                          PDF
                        </a>
                      </Button>
                    )}
                  </Flex>
                </Flex>
              }
            />
          ))}
        </CellList>
      )}
    </Panel>
  );
}
